import { Alert, Button, EmptyState, ResponsiveList, StatusBadge, formatDate, useMediaQuery } from '@catering/ui';
import { Link } from '@tanstack/react-router';
import type { JSX } from 'react';
import { useState } from 'react';

import { useCurrentUser } from '../auth/use-current-user';
import { usePortalMe } from '../shell/use-portal-me';
import { describeOrderError } from './error-messages';
import { Order, OrderStatus } from './api';
import { hasOrderPermission } from './order-permissions';
import { useOrderList } from './use-order-list';

const PAGE_SIZE = 50;

const STATUS_OPTIONS: { value: OrderStatus; label: string }[] = [
  { value: 'DRAFT', label: 'Borrador' },
  { value: 'SUBMITTED', label: 'Enviado' },
  { value: 'CONFIRMED', label: 'Confirmado' },
  { value: 'CANCELLED', label: 'Cancelado' },
];

/**
 * `portal-orders/spec.md` → "Historial de pedidos con filtros y alcance
 * del usuario" (RF-06-12): filtros por sede, estado y rango de fechas,
 * siempre dentro del `DataScope` del usuario.
 */
export function OrderListPage(): JSX.Element {
  const me = usePortalMe(true);
  const currentUser = useCurrentUser();
  const isDesktop = useMediaQuery('(min-width: 640px)');
  const canManageTemplates = hasOrderPermission(
    currentUser.data?.roleCodes ?? [],
    'order.template.manage',
  );
  const [siteId, setSiteId] = useState('');
  const [operatingUnitId, setOperatingUnitId] = useState('');
  const [status, setStatus] = useState('');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);

  const dateRangeInvalid = dateFrom !== '' && dateTo !== '' && dateTo < dateFrom;

  const filter = {
    ...(siteId !== '' ? { siteId } : {}),
    ...(operatingUnitId !== '' ? { operatingUnitId } : {}),
    ...(status !== '' ? { status: status as OrderStatus } : {}),
    ...(dateFrom !== '' && !dateRangeInvalid ? { dateFrom } : {}),
    ...(dateTo !== '' && !dateRangeInvalid ? { dateTo } : {}),
    page,
    pageSize: PAGE_SIZE,
  };

  const orders = useOrderList(filter);
  const sites = me.data?.status === 'ACTIVE' ? me.data.sites : [];
  const operatingUnits = me.data?.status === 'ACTIVE' ? (me.data.operatingUnits ?? []) : [];
  const showUnits = operatingUnits.length > 1;

  function updateFilter(next: () => void): void {
    next();
    setPage(1);
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-semibold text-slate-900">Historial de pedidos</h1>
        {!isDesktop && canManageTemplates ? (
          <Link to="/orders/templates" className="inline-flex">
            <Button variant="secondary" size="sm">
              Plantillas
            </Button>
          </Link>
        ) : null}
      </div>

      <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap">
        {showUnits ? (
          <div className="flex flex-col gap-1.5">
            <label htmlFor="filter-operating-unit" className="text-sm font-medium text-slate-900">
              Punto de venta
            </label>
            <select
              id="filter-operating-unit"
              value={operatingUnitId}
              onChange={(event) => updateFilter(() => setOperatingUnitId(event.target.value))}
              className="h-11 rounded-md border border-slate-300 px-3 text-sm"
            >
              <option value="">Todos los puntos de venta</option>
              {operatingUnits.map((unit) => (
                <option key={unit.id} value={unit.id}>
                  {unit.name}
                </option>
              ))}
            </select>
          </div>
        ) : null}

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-site" className="text-sm font-medium text-slate-900">
            Sede
          </label>
          <select
            id="filter-site"
            value={siteId}
            onChange={(event) => updateFilter(() => setSiteId(event.target.value))}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Todas las sedes</option>
            {sites.map((site) => (
              <option key={site.id} value={site.id}>
                {site.name}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-status" className="text-sm font-medium text-slate-900">
            Estado
          </label>
          <select
            id="filter-status"
            value={status}
            onChange={(event) => updateFilter(() => setStatus(event.target.value))}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          >
            <option value="">Todos los estados</option>
            {STATUS_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-date-from" className="text-sm font-medium text-slate-900">
            Desde
          </label>
          <input
            id="filter-date-from"
            type="date"
            value={dateFrom}
            onChange={(event) => updateFilter(() => setDateFrom(event.target.value))}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          />
        </div>

        <div className="flex flex-col gap-1.5">
          <label htmlFor="filter-date-to" className="text-sm font-medium text-slate-900">
            Hasta
          </label>
          <input
            id="filter-date-to"
            type="date"
            value={dateTo}
            onChange={(event) => updateFilter(() => setDateTo(event.target.value))}
            className="h-11 rounded-md border border-slate-300 px-3 text-sm"
          />
        </div>
      </div>

      {dateRangeInvalid ? (
        <p role="alert" className="text-xs text-red-600">
          La fecha "Hasta" debe ser posterior o igual a "Desde"
        </p>
      ) : null}

      {orders.isPending ? <p className="text-sm text-slate-500">Cargando…</p> : null}

      {orders.isError ? (
        <Alert tone="danger">
          No pudimos cargar el historial de pedidos.
          <div className="mt-2">
            <Button size="sm" onClick={() => void orders.refetch()}>
              Reintentar
            </Button>
          </div>
          <p className="mt-1 text-xs text-slate-500">{describeOrderError(orders.error)}</p>
        </Alert>
      ) : null}

      {orders.data !== undefined ? (
        <>
          <ResponsiveList
            items={orders.data.items}
            rowKey={(order) => order.id}
            caption="Historial de pedidos"
            getRowTestId={(order) => `order-card-${order.id}`}
            emptyState={
              <EmptyState
                headingLevel="h2"
                title="Todavía no hay pedidos para estos filtros."
                description="Probá con otra sede, estado o fecha."
              />
            }
            columns={[
              { key: 'orderNumber', header: 'Número', render: (order: Order) => order.orderNumber },
              ...(showUnits
                ? [
                    {
                      key: 'operatingUnitName',
                      header: 'Punto de venta',
                      render: (order: Order) => order.operatingUnitName ?? '—',
                    },
                  ]
                : []),
              { key: 'siteName', header: 'Sede', render: (order: Order) => order.siteName },
              {
                key: 'serviceDate',
                header: 'Fecha',
                render: (order: Order) => formatDate(order.serviceDate),
              },
              {
                key: 'status',
                header: 'Estado',
                render: (order: Order) => <StatusBadge domain="order" status={order.status} />,
              },
            ]}
            renderCardHeader={(order: Order) => (
              <Link to="/orders/$orderId" params={{ orderId: order.id }} className="block">
                {order.orderNumber}
              </Link>
            )}
            renderCardBody={(order: Order) => (
              <Link to="/orders/$orderId" params={{ orderId: order.id }} className="contents">
                {showUnits ? (
                  <>
                    <span className="text-slate-500">Punto de venta</span>
                    <span>{order.operatingUnitName ?? '—'}</span>
                  </>
                ) : null}
                <span className="text-slate-500">Sede</span>
                <span>{order.siteName}</span>
                <span className="text-slate-500">Fecha</span>
                <span>{formatDate(order.serviceDate)}</span>
                <span className="text-slate-500">Estado</span>
                <StatusBadge domain="order" status={order.status} />
              </Link>
            )}
            {...(isDesktop
              ? {
                  renderActions: (order: Order) => (
                    <Link
                      to="/orders/$orderId"
                      params={{ orderId: order.id }}
                      className="underline"
                    >
                      Ver detalle
                    </Link>
                  ),
                }
              : {})}
          />

          {orders.data.items.length > 0 ? (
            <div className="flex items-center justify-between gap-3">
              <Button
                variant="secondary"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage((current) => current - 1)}
              >
                Página anterior
              </Button>
              <span className="text-sm text-slate-500">Página {page}</span>
              <Button
                variant="secondary"
                size="sm"
                disabled={page * PAGE_SIZE >= orders.data.total}
                onClick={() => setPage((current) => current + 1)}
              >
                Página siguiente
              </Button>
            </div>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
